import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSearch } from './useSearch';
import {
	Box,
	Paper,
	List,
	ListItem,
	Typography,
	InputBase,
	Popper
} from '@mui/material';
import { useTheme } from "@mui/material/styles";
import { GridSearchIcon } from '@mui/x-data-grid';
import { toUrl } from './toURL';
import { SearchRecord } from './types';
import { usePepwiseAuthController } from 'Context/contexts/AuthContext';
import useDataEstateSideDrawer from 'components/DataEstate/SideDrawers/useDrawerComponent';
import { useFinOpsDrawer } from 'Context/contexts/FinOpsDrawerContext';
import { useSearchContext } from 'Context/contexts/searchContext';
import { hideML } from '../../service/config'

export default function GlobalSearch() {
	const theme= useTheme();
	const colors = theme.palette;
	const navigate = useNavigate();
	const [controller] = usePepwiseAuthController();
	const { capabilities } = controller;
	const { query, setQuery, results, setResults, handleSearch } = useSearch();
	const inputRef = useRef<HTMLInputElement | null>(null);
	const popperRef = useRef<HTMLDivElement | null>(null);
  	const anchorRef = useRef<HTMLDivElement | null>(null);
	const [isOpen, setIsOpen] = useState(false);
	const [selectedPath, setSelectedPath] = useState("");
	const { handleSetComponent } = useDataEstateSideDrawer();
	const { openDrawer } = useFinOpsDrawer();
	const { setSearchTerm, setSelectedTerm } = useSearchContext();

	const platformObsCapabilities = [
	'EDW Jobs',
	'Tableau',
	'Data Growth',
	'Databricks',
	'Presto',
	'Power BI',
	'Data Management Framework',
	'Controls & Compliance',
	'Verification & Validation'
	];

	const isPlatformObsVisible = platformObsCapabilities.some((cap) =>{
		return (capabilities ?? []).includes(cap);
	});

	const filteredResults = results.filter((result: SearchRecord) => {
		const module = result.module;

		if (module === 'D&AI Executive') {
			return capabilities.includes('Data Estate');
		}

		if (module === 'D&AI Monthly XOps') {
			return (
			capabilities.includes('Data Estate', 'Fin Ops') ||
			isPlatformObsVisible
			);
		}

		if (module === 'Data Pipeline') {
			return capabilities.includes('Data Ops');
		}

		if (module === 'Program Executive') {
			return capabilities.includes('IBP');
		}

		if (module === 'Finops') {
			return capabilities.includes('Fin Ops');
		}

		if (module === 'FinOps Executive') {
			return capabilities.includes('FinOps Executive');
		}

		if (module === 'Platform Observability') {
			return isPlatformObsVisible;
		}

		if (module === 'ML Observability' && !hideML) {
			return capabilities.includes('Machine Learning');
		}

		if (module === 'Data KPI and Alerts') {
			return capabilities.includes('Data KPI and Alerts');
		}
		if (module === 'Product Catalog') {
			return capabilities.includes('Product Catalog');
		}
	});

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		setSearchTerm(value);
		setQuery(value);

		if (value.length >= 3) {
			handleSearch(value);
			setIsOpen(true);
		} else {
			setResults([]);
			setIsOpen(false);
		}
	};

	const location = useLocation();

	const handleSelect = (result: SearchRecord) => {
		setSelectedTerm(result.label);
		const url = toUrl(result);
		const [pathname, hashPart] = url.split("#");
		setSelectedPath(pathname);
		const hash = hashPart ? decodeURIComponent(hashPart) : "";

		const scrollToHash = () => {
			if (!hash) return;
			let tries = 0;
			const tryScroll = () => {
			const el = document.getElementById(hash);
			if (el) {
				el.scrollIntoView({ behavior: "smooth", block: "center" });
			} else if (tries++ < 20) {
				setTimeout(tryScroll, 100);
			}
			};
			tryScroll();
		};

		if (result.drawerType === "FINOPS_COST_BREAKUP" && result.drawerProps) {
			navigate(pathname);
			setTimeout(() => {
				openDrawer(result.drawerProps!.title);
			}, 300);
			setIsOpen(false);
			return;
		}

		// Case 1: Overlay (drawer) item
		if (result.drawerType && result.drawerProps) {
			navigate(pathname);

			// Wait for page navigation, then open overlay + scroll
			setTimeout(() => {
			handleSetComponent(result.drawerType!, result.drawerProps!);
			scrollToHash();
			}, 400);

			setIsOpen(false);
			return;
		}

		// Case 2: regular route navigation
		if (!hash) {
			// no anchor: normal navigation
			navigate(pathname);
			setIsOpen(false);
			return;
		}

		if (location.pathname === pathname) {
			// same page: just update the hash and scroll
			history.pushState(null, "", `#${hash}`);
			scrollToHash();
			setIsOpen(false);
		} else {
			navigate(`${pathname}#${hash}`);
			setTimeout(scrollToHash, 200);
			setIsOpen(false);
		}
	};

	useEffect(() => {
		if (selectedPath && location.pathname !== selectedPath) {
			setSearchTerm("");
			setQuery("");
			setSelectedTerm("");
			setSelectedPath("");
		}
	}, [location.pathname, selectedPath]);


	// Function to highlight matched text
	const highlightMatch = (text: string, query: string) => {
		if (!query) return text;
		const parts = text.split(new RegExp(`(${query})`, 'gi'));
		return parts.map(
			(part, index) =>
				// part.toLowerCase() === query.toLowerCase() ? (
				// 	<span key={index} style={{ backgroundColor: '#0390f5' }}>
				// 		{part}
				// 	</span>
				// ) : (
				part
			// )
		);
	};

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				popperRef.current &&
				!popperRef.current.contains(event.target as Node) &&
				!inputRef.current?.contains(event.target as Node)
			) {
				setIsOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);
	
	return (
		<Box id= "header-search-bar" ref={anchorRef} sx={{ position: 'relative' }}>
			<InputBase
				placeholder="Search"
				value={query}
				onChange={handleChange}
				inputRef={inputRef}
				sx={{
					color: 'textLight.light',
					backgroundColor: 'backgroundLight.globalSearch',
					pl: 5,
					pr: 1,
					height: 40,
					width: { xs: 160, sm: 220, md: 260, lg: 300 },
					'& .MuiInputBase-input::placeholder': {
						...theme.typography.filterText,
						color: '#8E99A6',
						opacity: 1
					},
				}}
				disabled={location.pathname === "/dashboard/sessionTimeOut"}
			/>

			{/* Search Icon */}
			<GridSearchIcon
				style={{
					position: 'absolute',
					top: '50%',
					left: 8,
					transform: 'translateY(-50%)',
					fontSize: 20,
					color: `${colors.textLight.light}`
				}}
			/>

			<Popper
				open={isOpen && filteredResults.length > 0}
				anchorEl={anchorRef.current}
				placement="bottom-start" // put it right below
				disablePortal={false} // render in a portal (on top of header)
				modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
				style={{ zIndex: 1500 }}
        		ref={popperRef}
			>
				<Paper
					sx={{
						maxHeight: 400,
						overflowY: 'auto',
						backgroundColor: `${colors.backgroundLight.globalSearch} !important`,
						color: `${colors.textLight.light} !important`,
            			fontSize: { xs: 12, sm: 13, md: 16 },

						"&::-webkit-scrollbar": {
						width: "8px",
						},
						"&::-webkit-scrollbar-track": {
						backgroundColor: `${colors.scrollbar.background} !important`,
						},
						"&::-webkit-scrollbar-thumb": {
						backgroundColor: `${colors.scrollbar.thumb} !important`,
						borderRadius: "4px",
						},
						"&::-webkit-scrollbar-thumb:hover": {
						backgroundColor: `${colors.scrollbar.thumb} !important`,
						},
					}}
				>
					<List sx={{ padding: 0 }}>
						{filteredResults.map((result: any) => (
							<ListItem
								key={result.id}
								onClick={() => handleSelect(result)}
								sx={{
									flexDirection: 'column',
									alignItems: 'flex-start',
									cursor: 'pointer',
									borderBottom: '0.5px solid #EFF6FF',
									width: { xs: 160, sm: 220, md: 260, lg: 300 },
									backgroundColor: `${colors.backgroundLight.globalSearch} !important`,
									color: `${colors.textLight.light} !important`,
									'&:hover': { backgroundColor: `${colors.backgroundLight.dark} !important` }
								}}
							>
								<Typography variant='bodyText'>
									{highlightMatch(result.label, query)}
								</Typography>
								<Typography variant="caption" color="textLight.light">
									{result.breadcrumb}
								</Typography>
							</ListItem>
						))}
					</List>
				</Paper>
			</Popper>
		</Box>
	);
}